// src/hooks/useRatingDialog.ts
import { useState, useCallback, useRef, useEffect } from 'react';
import showRatingDialog from '../utils/showRatingDialog';

interface UseRatingDialogParams {
  skipCondition?: boolean; // Optional condition to skip the dialog (such as isOnboarding)
  delayInSeconds?: number; // Wait time before showing the dialog
  daysToWait?: number; // Days to wait before showing again
}

/**
 * Custom hook to handle the rating dialog logic
 */
export function useRatingDialog({ skipCondition = false, daysToWait = 3 }: UseRatingDialogParams = {}) {
  const [isFeedbackTakeoverShown, setIsFeedbackTakeoverShown] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const triggerRatingDialog = useCallback(() => {
    const hasAcceptedReview = localStorage.getItem('user_accepted_review') === 'true';
    if (hasAcceptedReview || skipCondition) {
      return;
    }

    const today = new Date();
    const lastDeclineDate = localStorage.getItem('user_declined_review_date');
    const lastDeclineDateObject = lastDeclineDate ? new Date(lastDeclineDate) : null;

    const pastDate = new Date();
    pastDate.setDate(today.getDate() - daysToWait);

    if (!lastDeclineDateObject || lastDeclineDateObject < pastDate) {
      showRatingDialog(true).then((result) => {
        if (isMountedRef.current && result === false) {
          setIsFeedbackTakeoverShown(true);
        }
      });
    }
  }, [skipCondition, daysToWait]);

  return { isFeedbackTakeoverShown, setIsFeedbackTakeoverShown, triggerRatingDialog };
}
